import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { components } from 'react-select';
import { VariableSizeList as List } from 'react-window';
import { arrayOf, node, oneOfType } from 'prop-types';
import { isIE11 } from "../../utils/detectDevice";
import { getMethodsForTestId } from "../../utils/getMethodsForTestId";
import { isArray, isNumber } from "../../utils/type";
import ScrollbarBase from '../../Scrollbar';
import { selectPropsPropTypes } from '../shapes';
var SCROLLBAR_OPTIONS = {
  suppressScrollX: true,
  swipeEasing: true
};

var getIsFocused = function getIsFocused() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$props = _ref.props;

  _ref$props = _ref$props === void 0 ? {} : _ref$props;
  var isFocused = _ref$props.isFocused;
  return isFocused === true;
};

var getFocusedIndex = function getFocusedIndex(children) {
  return isArray(children) && Math.max(children.findIndex(getIsFocused), 0);
};

var calculateMenuHeight = function calculateMenuHeight(_ref2) {
  var isAutoOptionHeight = _ref2.isAutoOptionHeight,
      optionHeights = _ref2.optionHeights,
      children = _ref2.children,
      isMobile = _ref2.isMobile,
      maxMenuHeight = _ref2.maxMenuHeight,
      rowHeight = _ref2.rowHeight,
      isGroup = _ref2.isGroup;
  var menuHeight = maxMenuHeight || (isMobile ? 400 : 200);

  if (isGroup) {
    menuHeight += rowHeight;
  }

  if (children.length) {
    var optionHeight;

    if (isAutoOptionHeight) {
      optionHeight = children.reduce(function (acc, option) {
        return acc + (optionHeights[option.props.value] || {
          height: rowHeight
        }).height;
      }, 0);
    } else if (isGroup) {
      optionHeight = children.reduce(function (groupAcc, item) {
        var _item$props;

        var newAcc = groupAcc + rowHeight;
        return newAcc + (item === null || item === void 0 ? void 0 : (_item$props = item.props) === null || _item$props === void 0 ? void 0 : _item$props.children.reduce(function (optionsAcc) {
          return optionsAcc + rowHeight;
        }, 0));
      }, 0);
    } else {
      optionHeight = children.length * rowHeight;
    }

    if (optionHeight < menuHeight) {
      menuHeight = optionHeight;
    }
  }

  return menuHeight;
};

var calculateScrollOffset = function calculateScrollOffset(_ref3) {
  var children = _ref3.children,
      index = _ref3.index,
      isAutoOptionHeight = _ref3.isAutoOptionHeight,
      optionHeights = _ref3.optionHeights,
      rowHeight = _ref3.rowHeight;

  if (!isAutoOptionHeight) {
    return index * rowHeight;
  }

  return children.map(function (option) {
    return optionHeights[option.props.value];
  }).filter(function (option) {
    return (option === null || option === void 0 ? void 0 : option.index) < index;
  }).reduce(function (acc, option) {
    return acc + option.height;
  }, 0);
};

export var MenuList = function MenuList(_ref4) {
  var children = _ref4.children,
      props = _objectWithoutProperties(_ref4, _excluded);

  var _props$selectProps = props.selectProps,
      isAutoOptionHeight = _props$selectProps.isAutoOptionHeight,
      isDisplayedSearch = _props$selectProps.isDisplayedSearch,
      isGroup = _props$selectProps.isGroup,
      isMobile = _props$selectProps.isMobile,
      isSearchable = _props$selectProps.isSearchable,
      keyDownCounter = _props$selectProps.keyDownCounter,
      maxMenuHeight = _props$selectProps.maxMenuHeight,
      menuListProps = _props$selectProps.menuListProps,
      menuVariant = _props$selectProps.menuVariant,
      optionHeights = _props$selectProps.optionHeights,
      options = _props$selectProps.options,
      setIsDisplayedSearch = _props$selectProps.setIsDisplayedSearch,
      shouldScrollIntoView = _props$selectProps.shouldScrollIntoView,
      value = _props$selectProps.value,
      autoMobileMenuHeight = _props$selectProps.autoMobileMenuHeight,
      namespace = _props$selectProps.namespace;
  var listRef = useRef();
  var scrollRef = useRef();
  var rowHeights = useRef({});
  var rowHeight = isMobile ? 57 : 44;
  var rowCount = children.length || 0;
  var menuHeight = calculateMenuHeight({
    isAutoOptionHeight: isAutoOptionHeight,
    optionHeights: optionHeights,
    children: children,
    isMobile: isMobile,
    maxMenuHeight: maxMenuHeight,
    rowHeight: rowHeight,
    isGroup: isGroup
  });

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'menuList'),
      getTestId = _getMethodsForTestId.getTestId;

  var handleScroll = useCallback(function (container) {
    if (isMobile && container && !isDisplayedSearch) {
      if (!isAutoOptionHeight && container.scrollOffset > 0 || isAutoOptionHeight && container.scrollTop > 0) {
        setIsDisplayedSearch(true);
      }
    }
  }, [isMobile, isDisplayedSearch, isAutoOptionHeight, setIsDisplayedSearch]);
  useEffect(function () {
    if (!isMobile) {
      var index = getFocusedIndex(children);

      if (listRef.current !== null && isNumber(index)) {
        /*
          Решение для бага https://s7airlines.atlassian.net/browse/DS-1308
          Проблема кроется в библиотеке react-window, для windows chrome
          При единичном вызове scrollToItem или scrollTo происходит замирание прокрутки
          странность в том что даже сброс не внутренней функцией _resetIsScrolling
          не помогает https://github.com/bvaughn/react-window/blob/master/src/createListComponent.js#L623
          двойной вызов scrollToItem или двойной scrollTo так же не решает.
          При этом всём очень занимательно тем что при единичном scrollToItem
          замирание происходит в положении прокрутки 3+ элемента а scrollTo 1-3 элемента.
        */
        listRef.current.scrollToItem(index);
        listRef.current.scrollTo(calculateScrollOffset({
          children: children,
          index: index,
          isAutoOptionHeight: isAutoOptionHeight,
          optionHeights: optionHeights,
          rowHeight: rowHeight
        }));
      }
    }
  }, [isMobile, keyDownCounter]); // eslint-disable-line react-hooks/exhaustive-deps
  // Отключено для избежания циклического scroll к активному элементу

  useEffect(function () {
    if (isSearchable && value && shouldScrollIntoView && scrollRef.current !== null) {
      var selected = isArray(value) ? value[0] : value;
      var index = options.findIndex(function (option) {
        return option.value === selected.value;
      });

      if (index && scrollRef.current) {
        scrollRef.current.scrollTop = calculateScrollOffset({
          children: children,
          index: index,
          isAutoOptionHeight: isAutoOptionHeight,
          optionHeights: optionHeights,
          rowHeight: rowHeight
        });
      }
    }
  }, [scrollRef.current]); // eslint-disable-line react-hooks/exhaustive-deps
  // Отключено для избежания циклического scroll к активному элементу

  var commonScrollProps = _objectSpread(_objectSpread({
    borderRadius: !isMobile && menuVariant === 2 ? 8 : '0 0 8px 8px',
    options: SCROLLBAR_OPTIONS
  }, !autoMobileMenuHeight && {
    maxHeight: maxMenuHeight
  }), menuListProps); // eslint-disable-next-line react-hooks/exhaustive-deps


  var Scrollbar = useCallback( /*#__PURE__*/forwardRef(function (_ref5, // eslint-disable-line
  ref) {
    var style = _ref5.style,
        children = _ref5.children,
        onScroll = _ref5.onScroll;
    return /*#__PURE__*/React.createElement(ScrollbarBase, _extends({}, commonScrollProps, {
      onScroll: onScroll,
      ref: ref,
      style: _objectSpread(_objectSpread({}, style), {}, {
        borderRadius: commonScrollProps.borderRadius
      })
    }, getTestId()), children);
  }), [isMobile, menuVariant, listRef.current]);

  var getRowHeight = function getRowHeight(index) {
    return rowHeights.current[index] || rowHeight;
  };

  var setRowHeight = function setRowHeight(index, size) {
    listRef.current.resetAfterIndex(0);
    rowHeights.current = _objectSpread(_objectSpread({}, rowHeights.current), {}, _defineProperty({}, index, size));
  }; // eslint-disable-next-line react/prop-types


  var Row = function Row(_ref6) {
    var data = _ref6.data,
        index = _ref6.index,
        style = _ref6.style;
    var rowRef = useRef();
    useEffect(function () {
      if (rowRef.current) {
        setRowHeight(index, rowRef.current.clientHeight);
      }
    }, [index, rowRef]);
    return /*#__PURE__*/React.createElement("div", {
      style: style
    }, /*#__PURE__*/React.createElement("div", _extends({
      ref: rowRef
    }, getTestId('option', index)), data[index]));
  };

  if (!children.length) {
    return /*#__PURE__*/React.createElement(components.MenuList, props, children);
  }

  return /*#__PURE__*/React.createElement(components.MenuList, props, /*#__PURE__*/React.createElement(List, {
    ref: listRef,
    outerRef: scrollRef,
    outerElementType: isIE11 ? null : Scrollbar,
    height: menuHeight,
    itemCount: rowCount,
    itemData: children,
    itemSize: getRowHeight,
    onScroll: handleScroll
  }, Row));
};
MenuList.propTypes = process.env.NODE_ENV !== "production" ? {
  children: oneOfType([arrayOf(node), node]).isRequired,
  selectProps: selectPropsPropTypes
} : {};